import React from 'react';
import './AlgorithmTab.css';

export const AlgorithmTab: React.FC = () => {
  return (
    <div className="algorithm-tab">
      <div className="algorithm-container">
        <h2>🔬 Algorithm & Data Sources</h2>
        
        <div className="section">
          <h3>📊 Data Sources</h3>
          <div className="data-sources">
            <div className="data-source">
              <h4>Primary Dataset: Damodaran + FRED</h4>
              <ul>
                <li><strong>Equity Returns:</strong> S&P 500 total returns (1928-2024)</li>
                <li><strong>Treasury Bonds:</strong> 10-year US Treasury bonds (1928-2024)</li>
                <li><strong>Corporate Bonds:</strong> Aaa corporate bonds from FRED (1924-2024)</li>
                <li><strong>Inflation:</strong> Consumer Price Index (1928-2024)</li>
              </ul>
              <div className="source-links">
                <a href="https://pages.stern.nyu.edu/~adamodar/pc/datasets/histretSP.xlsx" target="_blank" rel="noopener noreferrer">
                  📈 Damodaran Historical Data
                </a>
                <a href="https://fred.stlouisfed.org/series/AAA" target="_blank" rel="noopener noreferrer">
                  🏛️ FRED Aaa Corporate Bonds
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="section">
          <h3>🧮 Historical Backtesting Algorithm</h3>
          <div className="pseudocode">
            <pre>{`FUNCTION historicalBacktest(startBalance, withdrawalRate, years, portfolioRatio):
  FOR each possible start year in historical data:
    balance = startBalance
    cumulativeInflation = 1.0
    initialRealWithdrawal = (withdrawalRate / 100) * startBalance
    
    FOR each year in simulation period:
      // 1. Calculate inflation-adjusted spending
      cumulativeInflation *= (1 + historicalInflation[year])
      desiredSpending = initialRealWithdrawal * cumulativeInflation
      
      // 2. Calculate social security (if applicable)
      socialSecurity = recurringIncome * cumulativeInflation
      
      // 3. Calculate taxes
      netFromPortfolio = MAX(0, desiredSpending - socialSecurity)
      taxes = calculateTaxes(netFromPortfolio, socialSecurity, cumulativeInflation)
      totalWithdrawal = netFromPortfolio + taxes
      
      // 4. Withdraw money FIRST (realistic order)
      withdrawal = MIN(balance, totalWithdrawal)
      balance -= withdrawal
      
      // 5. Apply portfolio returns AFTER withdrawals
      equityReturn = historicalEquityReturns[year]
      bondReturn = calculateBondReturn(portfolioRatio, year)
      portfolioReturn = portfolioRatio.equity * equityReturn + 
                       portfolioRatio.bonds * bondReturn
      balance *= (1 + portfolioReturn)
      
      // 6. Check for failure
      IF balance <= 0:
        markAsFailed()
        BREAK
    
    recordOutcome(balance, failed)
  
  RETURN successRate, statistics`}</pre>
          </div>
        </div>

        <div className="section">
          <h3>🎲 Monte Carlo Simulation Algorithm</h3>
          <div className="pseudocode">
            <pre>{`FUNCTION monteCarloSimulation(startBalance, withdrawalRate, years, runs):
  FOR each simulation run (1 to runs):
    balance = startBalance
    cumulativeInflation = 1.0
    initialRealWithdrawal = (withdrawalRate / 100) * startBalance
    
    FOR each year in simulation period:
      // 1. Draw random inflation and returns
      inflation = drawFromInflationDistribution()
      realEquityReturn = drawFromEquityDistribution()
      realBondReturn = drawFromBondDistribution()
      
      // 2. Calculate inflation-adjusted spending
      cumulativeInflation *= (1 + inflation)
      desiredSpending = initialRealWithdrawal * cumulativeInflation
      
      // 3. Calculate social security and taxes
      socialSecurity = recurringIncome * cumulativeInflation
      netFromPortfolio = MAX(0, desiredSpending - socialSecurity)
      taxes = calculateTaxes(netFromPortfolio, socialSecurity, cumulativeInflation)
      totalWithdrawal = netFromPortfolio + taxes
      
      // 4. Withdraw money FIRST
      withdrawal = MIN(balance, totalWithdrawal)
      balance -= withdrawal
      
      // 5. Apply portfolio returns AFTER withdrawals
      portfolioReturn = portfolioRatio.equity * realEquityReturn + 
                       portfolioRatio.bonds * realBondReturn
      nominalReturn = (1 + portfolioReturn) * (1 + inflation) - 1
      balance *= (1 + nominalReturn)
      
      // 6. Check for failure
      IF balance <= 0:
        markAsFailed()
        BREAK
    
    recordOutcome(balance, failed)
  
  RETURN successRate, percentiles, statistics`}</pre>
          </div>
        </div>

        <div className="section">
          <h3>🔧 Key Features</h3>
          <div className="features">
            <div className="feature">
              <h4>Realistic Withdrawal Timing</h4>
              <p>Withdrawals happen at the beginning of each year, before returns are applied. This matches real-world behavior where you need money to live on throughout the year.</p>
            </div>
            
            <div className="feature">
              <h4>Inflation-Adjusted Withdrawals</h4>
              <p>Withdrawal amounts increase each year by the historical inflation rate to maintain purchasing power, following the Trinity Study methodology.</p>
            </div>
            
            <div className="feature">
              <h4>Comprehensive Tax Modeling</h4>
              <p>Calculates federal income taxes on portfolio withdrawals and social security benefits using current tax brackets and rules.</p>
            </div>
            
            <div className="feature">
              <h4>Mixed Bond Allocations</h4>
              <p>Supports custom Treasury/corporate bond allocations, allowing you to match your actual 401k target-date fund composition.</p>
            </div>
            
            <div className="feature">
              <h4>Sequence of Returns Risk</h4>
              <p>Both historical and Monte Carlo simulations account for the critical impact of market timing on retirement success.</p>
            </div>
          </div>
        </div>

        <div className="section">
          <h3>📈 Mathematical Formula</h3>
          <div className="formula">
            <p><strong>Portfolio Balance After Year t:</strong></p>
            <div className="math-formula">
              B<sub>t</sub> = (B<sub>t-1</sub> - W<sub>t</sub>) × (1 + R<sub>t</sub>)
            </div>
            <p>Where:</p>
            <ul>
              <li>B<sub>t</sub> = Portfolio balance at end of year t</li>
              <li>W<sub>t</sub> = Withdrawal amount for year t (inflation-adjusted)</li>
              <li>R<sub>t</sub> = Portfolio return for year t</li>
            </ul>
            
            <p><strong>Withdrawal Amount:</strong></p>
            <div className="math-formula">
              W<sub>t</sub> = W<sub>0</sub> × ∏<sub>i=1</sub><sup>t</sup>(1 + π<sub>i</sub>)
            </div>
            <p>Where:</p>
            <ul>
              <li>W<sub>0</sub> = Initial withdrawal (withdrawalRate × startBalance)</li>
              <li>π<sub>i</sub> = Inflation rate for year i</li>
            </ul>
          </div>
        </div>

        <div className="section">
          <h3>🔍 Validation & Comparison</h3>
          <p>Our algorithm is validated against the Trinity Study methodology and uses the same core principles:</p>
          <ul>
            <li>✅ Fixed real withdrawal amounts (inflation-adjusted)</li>
            <li>✅ Historical data backtesting</li>
            <li>✅ Monte Carlo simulation for statistical analysis</li>
            <li>✅ Sequence of returns risk modeling</li>
            <li>✅ Multiple asset class support</li>
          </ul>
          <p><strong>Key Differences from Trinity Study:</strong></p>
          <ul>
            <li>📅 Extended time period (1928-2024 vs 1926-1995)</li>
            <li>🏛️ Mixed Treasury/corporate bond allocations</li>
            <li>💰 Comprehensive tax modeling</li>
            <li>📊 More detailed Monte Carlo statistics</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
